/**
 * **Channel Daily** — the client spec's tables 4 (English) and 7 (International),
 * nine columns: Channel · Date · Story Views · Spotlight Views · Viewers ·
 * Shares · Replies · Screenshots · Subscribers.
 *
 * Extracted from `pages/ChannelOverview.tsx` on 8 Sep so it can mount on **both**
 * `/channels` and `/stories`. The client's own doc files these two tables under
 * its `STORIES SUMMARY & COLUMNS` heading, while `APPLICATION_SPEC.md` §4.4 and
 * Phase 4 put them on Channel Overview — where Jesse pointed when he said Channel
 * Daily is the screen the whole design should match. **Both are right, so both
 * render them, from one component.** A second copy of a nine-column contract
 * would drift the first time a counting-basis caveat changed, and this table is
 * exactly where those caveats live (Q-C10: channel `VIEWERS` is deduplicated
 * across content types and differs from `STORY_UNIQUES` by 49% on a real day).
 *
 * Self-contained by design: it owns its `metric-notes` query rather than taking
 * the map as a prop, so a page can mount it without knowing what it needs.
 */
import { useQuery } from '@tanstack/react-query'
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { Pagination } from './AssetBits'
import { formatDate } from '../lib/dates'
import { channelDaysLink, channelLifetimeLink } from '../lib/rawFilters'
import { type Opener, RowControls, SelectedBar, SelectionBar, selectColumn } from './SendToRawData'
import { formatNumber } from '../lib/numbers'
import { HintBubble } from './Hint'
import { bodyCell, headCell, headStyle, numericCell, numericHeadCell } from './DataTable'
import { NO_ROWS } from '../lib/tableData'
import { InfoDot } from './InfoDot'
import { TableToolbar } from './TableToolbar'
import { Card, Note, Skeleton } from './ui'
import {
  api,
  DAILY_COLUMNS,
  DAILY_COLUMN_LABELS,
  type ChannelLifetimeRow,
  type DailyColumn,
  type DailyRow,
  type MetricNote,
} from '../lib/api'


/** Tabular figures, per the design system — numbers are Plus Jakarta Sans, not mono. */
const numberStyle = { fontVariantNumeric: 'tabular-nums' } as const

export type LanguageGroup = 'english' | 'international'

/** The four filled steps of the heatmap ramp, low → high. Shared with the
 *  legend so the swatches and the cells cannot drift apart, and written out
 *  rather than interpolated: `--seq-${n}` invents names like `--seq-250`, and an
 *  undefined custom property paints nothing at all. */
const HEAT_RAMP = ['var(--seq-100)', 'var(--seq-300)', 'var(--seq-500)', 'var(--seq-700)'] as const

/**
 * Why a column must not be added up, **in the reader's terms**.
 *
 * The fallback used to build this sentence from the registry — *"Viewers is
 * snap-basis and must never be summed across channels or days"* — which names
 * our counting-basis vocabulary and tells the reader what they may not do
 * without telling them what the number *is*. These say what it is; not being
 * addable follows from that.
 */
const NON_ADDITIVE: Partial<Record<DailyColumn, string>> = {
  VIEWERS: 'People, Not Views.',
  SUBSCRIBERS: 'The Total That Day, Not New Subscribers.',
}

/** For a metric the registry marks non-additive that has no note of its own. */
const GENERIC_NON_ADDITIVE = 'A Daily Figure. Adding These up Does Not Give a Total.'

const GROUP_TITLES: Record<LanguageGroup, string> = {
  english: 'English Channel Daily',
  international: 'International Channel Daily',
}

/**
 * **Channel × day heatmap** — `APPLICATION_SPEC.md` §4.2's fourth visual.
 *
 * 13 channels × dates is precisely the grid case the dataviz form heuristic
 * sends to a heatmap: **sequential, one hue, more-is-darker.** A 13-series line
 * chart is the alternative and it breaks both the ~7-class rule and the design
 * system's three-series cap.
 *
 * **Each row is scaled to its own maximum, and that is deliberate.** The
 * question this answers is §4.2's — *"where are the publishing gaps and
 * spikes?"* — and on a shared scale Phony Texts is dark, ten channels are the
 * palest step, and a gap on a small channel is invisible next to a gap on a
 * large one. Row scaling costs the cross-channel comparison, which is what the
 * table underneath is for: every chart here has a table-view twin, and this one
 * is sitting directly below it.
 *
 * **No data is not zero.** A day Snap returned no row for is drawn as the idle
 * surface with a diagonal rule, never as the palest ramp step — the distinction
 * between *published nothing* and *we never collected it* is the whole reason
 * `snap.ingest_coverage` exists.
 */
export function ViewsHeatmap({
  from,
  to,
  channels,
  groups = ['english', 'international'],
}: {
  from: string
  to: string
  channels: string[]
  /**
   * 🔴 **This prop did not exist, and that was ST-13.**
   *
   * Abi, 15 Sep: *"story views by channel and day is showing every channel, not
   * just the three it should be filtered to when english channels is selected."*
   *
   * The scope control writes `?group=english` and **clears `?channels=`** — they
   * are one control, deliberately (`lib/filters.ts`), because two live scope
   * controls is the duplication that design replaced. So a heatmap reading only
   * `channels` receives an empty array on every group selection and asks for the
   * whole estate. The tables beside it pass `group` and filtered correctly,
   * which is exactly why only the heatmap was wrong and why it survived review:
   * the screen looked internally inconsistent rather than broken.
   *
   * **Third instance of this one root cause** — `9fb96c5` fixed it on
   * `/channels` and `ChannelOverview.tsx:52` records the same finding. A query
   * keyed on `channels` that does not also read `group` is the bug's signature.
   */
  groups?: readonly LanguageGroup[]
}) {
  // One group selected means filter by it; both means the whole estate, which is
  // what the API takes absence to mean. There is no third case: `groups` is
  // either the page's single tab or the default pair.
  const group = groups.length === 1 ? groups[0] : undefined
  const query = useQuery({
    queryKey: ['channel-daily-heatmap', from, to, channels.join(','), group ?? 'all'],
    queryFn: () => api.channelDaily({ from, to, channels, group }),
  })

  const grid = useMemo(() => {
    const rows = query.data ?? []
    const days = [...new Set(rows.map((row) => row.stat_date))].sort()
    const byChannel = new Map<string, { label: string; values: Map<string, number | null> }>()
    for (const row of rows) {
      const entry = byChannel.get(row.handle) ?? {
        label: row.display_label,
        values: new Map<string, number | null>(),
      }
      entry.values.set(row.stat_date, row.metrics.STORY_VIEWS ?? null)
      byChannel.set(row.handle, entry)
    }
    return { days, channels: [...byChannel.entries()] }
  }, [query.data])

  /**
   * One bubble, driven by delegated `mouseover` on the table.
   *
   * The alternative — a `title` per cell — is what this replaces: ~400 cells
   * where hovering is the *only* way to read a value, each taking about a
   * second to answer. And a `Hint` component per cell would mount 400
   * listeners and 400 pieces of state for one bubble at a time.
   */
  const [hover, setHover] = useState<{ rect: DOMRect; label: string; value: string } | null>(null)

  if (query.isLoading) return <Skeleton rows={5} />
  if (query.error) return <Note tone="error">{(query.error as Error).message}</Note>
  if (grid.days.length === 0) return null

  return (
    <Card
      title="Story Views by Channel and Day"
      info="Darker Means a Busier Day for That Channel."
      action={
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-label)' }}>
          <span>Low</span>
          {HEAT_RAMP.map((step) => (
            <span
              key={step}
              aria-hidden="true"
              style={{
                width: '0.75rem',
                height: '0.75rem',
                background: step,
                borderRadius: '2px',
                display: 'inline-block',
              }}
            />
          ))}
          <span>High, per Channel</span>
        </div>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <table
          className="text-xs"
          style={{ borderCollapse: 'separate', borderSpacing: '1px' }}
          onMouseOver={(event) => {
            const cell = (event.target as HTMLElement).closest('td[data-label]')
            if (!cell) return
            setHover({
              rect: cell.getBoundingClientRect(),
              label: cell.getAttribute('data-label') ?? '',
              value: cell.getAttribute('data-value') ?? '',
            })
          }}
          onMouseLeave={() => setHover(null)}
        >
          <caption className="sr-only">
            Daily Story Views per Channel From {from} to {to}. Each Row Is Shaded Relative to That
            Channel's Own Busiest Day; Exact Values Are in the Channel Daily Table Below.
          </caption>
          <tbody>
            {grid.channels.map(([handle, entry]) => {
              const numbers = [...entry.values.values()].filter((v): v is number => v !== null)
              const max = Math.max(...numbers, 1)
              return (
                <tr key={handle}>
                  <th
                    scope="row"
                    className="pr-2 text-right font-normal"
                    style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                  >
                    {entry.label}
                  </th>
                  {grid.days.map((day) => {
                    const value = entry.values.get(day) ?? null
                    // Five steps, so a reader can tell them apart; a continuous
                    // ramp on a 10px cell is a gradient nobody can read off.
                    const step =
                      value === null
                        ? null
                        : value === 0
                          ? 0
                          : Math.min(4, Math.ceil((value / max) * 4))
                    const background =
                      step === null
                        ? 'var(--status-idle-bg)'
                        : step === 0
                          ? 'var(--grey-100)'
                          : HEAT_RAMP[step - 1]!
                    return (
                      <td
                        key={day}
                        data-label={`${entry.label} · ${day}`}
                        data-value={
                          value === null ? 'no row collected' : `${formatNumber(value)} story views`
                        }
                        tabIndex={0}
                        aria-label={`${entry.label}, ${formatDate(day)}, ${
                          value === null ? 'no row collected' : `${formatNumber(value)} story views`
                        }`}
                        onFocus={(event) =>
                          setHover({
                            rect: event.currentTarget.getBoundingClientRect(),
                            label: `${entry.label} · ${formatDate(day)}`,
                            value:
                              value === null
                                ? 'no row collected'
                                : `${formatNumber(value)} story views`,
                          })
                        }
                        onBlur={() => setHover(null)}
                        style={{
                          width: '0.7rem',
                          height: '0.9rem',
                          padding: 0,
                          background,
                          borderRadius: '2px',
                          // A missing day must not read as a quiet day.
                          backgroundImage:
                            value === null
                              ? 'linear-gradient(135deg, transparent 45%, var(--border) 45%, var(--border) 55%, transparent 55%)'
                              : undefined,
                        }}
                      />
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
        {hover && (
          <HintBubble rect={hover.rect}>
            <strong style={{ display: 'block' }}>{hover.label}</strong>
            <span style={{ ...numberStyle, color: 'var(--text-secondary)' }}>{hover.value}</span>
          </HintBubble>
        )}
      </div>
    </Card>
  )
}

export function ChannelDailyTables({
  from,
  to,
  channels,
  groups = ['english', 'international'],
  window = 'range',
}: {
  from: string
  to: string
  channels: string[]
  /** Which of the two contracted tables to render. Stories passes the group its
   *  own tab row has selected; Channel Overview renders both. */
  groups?: readonly LanguageGroup[]
  /**
   * **The client doc heads these tables *(Lifetime)*, and the word does two jobs.**
   *
   * `range` is *all collected days* — the contracted `Date` column, one row per
   * channel per day. `lifetime` is Snap's own `granularity=LIFETIME`: a
   * different API call, a different table, one row per channel and no date.
   *
   * They are not one dataset filtered two ways, and the figures do not
   * reconcile — Snap's lifetime window differs per metric. So they never share
   * a table.
   */
  window?: 'range' | 'lifetime'
}) {
  const notes = useQuery({
    queryKey: ['metric-notes'],
    queryFn: api.metricNotes,
    staleTime: Infinity,
  })
  const noteFor = useMemo(() => {
    const map = new Map<string, MetricNote>()
    for (const note of notes.data ?? []) map.set(note.metric_name, note)
    return map
  }, [notes.data])

  return (
    // `minmax(0, 1fr)`, not the default `1fr`. A grid item's implicit
    // `min-width: auto` sizes it to its content, so the widest table pushed its
    // own column past the viewport and scrolled the *body* by 508px on a phone —
    // the table's own `overflow-x: auto` never got the chance to do its job.
    <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
      {/* §4.2 asks for the heatmap *beside* these tables, and it is the same
          data at a different altitude: shape here, exact values below. On the
          lifetime window there is no day axis for it to draw. */}
      {window === 'range' && (
        <ViewsHeatmap from={from} to={to} channels={channels} groups={groups} />
      )}
      {groups.map((group) =>
        window === 'lifetime' ? (
          <LifetimeTable key={group} group={group} channels={channels} noteFor={noteFor} />
        ) : (
          <DailyTable
            key={group}
            group={group}
            from={from}
            to={to}
            channels={channels}
            noteFor={noteFor}
          />
        ),
      )}
    </div>
  )
}

/**
 * RD-21. Channel Daily rows are channel-days, so they open Channel Daily Stats
 * for those channels over the span of the selected days. One range covers the
 * whole selection — ticking 3 Aug and 20 Aug opens 3–20 Aug — and the bar says
 * so rather than implying only the ticked days come back.
 */
const CHANNEL_DAY_OPENER: Opener<DailyRow> = {
  open: (rows) => channelDaysLink(rows),
  label: (row) => `${row.display_label} on ${formatDate(row.stat_date)}`,
  noun: 'Selected',
  limit: 500,
  note: (rows) => {
    const days = [...new Set(rows.map((row) => row.stat_date))].sort()
    if (days.length < 2) return null
    const span =
      (Date.parse(days.at(-1)!) - Date.parse(days[0]!)) / 86_400_000 + 1
    return span > days.length
      ? `Opens ${formatDate(days[0]!)} to ${formatDate(days.at(-1)!)}, Every Day Between, for These Channels.`
      : null
  },
  menu: (row) => [
    { label: 'Channel Daily Stats, This Day', to: channelDaysLink([row]) },
    { label: 'Channel Lifetime Stats', to: channelLifetimeLink([row]) },
  ],
}

const CHANNEL_LIFETIME_OPENER: Opener<ChannelLifetimeRow> = {
  open: (rows) => channelLifetimeLink(rows),
  label: (row) => row.display_label,
  noun: 'Selected',
}

function DailyTable({
  group,
  from,
  to,
  channels,
  noteFor,
}: {
  group: LanguageGroup
  from: string
  to: string
  channels: string[]
  noteFor: Map<string, MetricNote>
}) {
  // Newest first: the question a reader arrives with is "what happened
  // yesterday", not "what happened on 20 July".
  const [sorting, setSorting] = useState<SortingState>([{ id: 'stat_date', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [search, setSearch] = useState('')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const query = useQuery({
    queryKey: ['channel-daily', group, from, to, channels.join(',')],
    queryFn: () => api.channelDaily({ from, to, group, channels }),
  })

  const columns = useMemo<ColumnDef<DailyRow>[]>(() => {
    const metricColumns = DAILY_COLUMNS.map((metric: DailyColumn) => ({
      id: metric,
      header: () => {
        const note = noteFor.get(metric)
        // Only a real caveat earns a dot. A dot on every column would make the
        // one that matters invisible.
        const caveat =
          note?.caveat ??
          (note && !note.aggregable ? NON_ADDITIVE[metric] ?? GENERIC_NON_ADDITIVE : null)
        return (
          <>
            {DAILY_COLUMN_LABELS[metric]}
            {caveat && <InfoDot note={caveat} />}
          </>
        )
      },
      accessorFn: (row: DailyRow) => row.metrics[metric] ?? null,
      cell: (info: { getValue: () => unknown }) => {
        const value = info.getValue() as number | null
        // Absent renders as an em dash, never as 0 — zero is a measurement.
        return value === null ? (
          <span style={{ color: 'var(--text-label)' }}>—</span>
        ) : (
          formatNumber(value)
        )
      },
      meta: { numeric: true },
    })) as ColumnDef<DailyRow>[]

    return [
      // First, like every other table that links into Raw Data (RD-21).
      selectColumn(CHANNEL_DAY_OPENER),
      { id: 'channel', header: 'Channel', accessorKey: 'display_label' },
      {
        id: 'stat_date',
        header: 'Date',
        accessorKey: 'stat_date',
        cell: (info) => formatDate(info.getValue() as string),
      },
      ...metricColumns,
    ]
  }, [noteFor])

  const table = useReactTable({
    data: query.data ?? NO_ROWS,
    columns,
    state: { sorting, columnFilters, globalFilter: search, rowSelection },
    getRowId: (row) => `${row.handle}|${row.stat_date}`,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Paginated like every other dense table. Unpaginated, International is
    // 490 rows for a seven-week window and grows by 10 a day.
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } },
  })

  const rows = table.getRowModel().rows
  const filtered = table.getFilteredRowModel().rows.length

  return (
    <Card
      title={GROUP_TITLES[group]}
      action={
        <span className="text-xs" style={{ color: 'var(--text-label)' }}>
          {query.isLoading ? '' : `${filtered} rows`}
        </span>
      }
      footer="Channel-Level Figures, One Row per Channel per Day."
    >
      {query.isLoading ? (
        <Skeleton rows={4} />
      ) : query.error ? (
        <Note tone="error">{(query.error as Error).message}</Note>
      ) : rows.length === 0 ? (
        <Note>
          No {group} Rows in This Range. The Channel Filter and This Table's Language Group
          Intersect, so Deselecting Every {group} channel above empties it.
        </Note>
      ) : (
        <>
          <SelectionBar table={table} opener={CHANNEL_DAY_OPENER} />
          <TableToolbar
            table={table}
            placeholder="Search Channel or Date"
            facets={[{ id: 'channel', label: 'Channel' }]}
          />
          {/* Vertical scroll belongs to the page, not to a box inside it: a
              30rem window over 490 rows swallows the wheel, and the page then
              refuses to scroll until the reader has gone through every row. */}
          <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const numeric = (header.column.columnDef.meta as { numeric?: boolean })?.numeric
                    return (
                      <th
                        key={header.id}
                        aria-sort={
                          header.column.getIsSorted() === 'asc'
                            ? 'ascending'
                            : header.column.getIsSorted() === 'desc'
                              ? 'descending'
                              : 'none'
                        }
                        style={{
                          ...headStyle(numeric),
                          // Sticky needs the table's own scroll container; the
                          // padding lives on the button so the whole header is
                          // the hit target.
                          position: 'sticky',
                          top: 0,
                          background: 'var(--bg-surface)',
                          padding: 0,
                        }}
                      >
                        {/* A real button, not a `<th onClick>`. The design
                            system's checklist requires keyboard reachability,
                            and a click handler on a table header is reachable
                            by mouse only — so sorting simply did not exist for
                            anyone navigating by keyboard. `aria-sort` on the
                            header then tells a screen reader the result. */}
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="w-full px-2 py-2 text-xs font-semibold uppercase"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            letterSpacing: 'inherit',
                            textTransform: 'inherit',
                            textAlign: numeric ? 'right' : 'left',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && ' ▲'}
                          {header.column.getIsSorted() === 'desc' && ' ▼'}
                        </button>
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const numeric = (cell.column.columnDef.meta as { numeric?: boolean })?.numeric
                    return (
                      <td
                        key={cell.id}
                        className="px-2"
                        style={{
                          height: 'var(--row-height-compact, 2rem)',
                          borderBottom: '1px solid var(--border)',
                          color: 'var(--text-primary)',
                          textAlign: numeric ? 'right' : 'left',
                          whiteSpace: 'nowrap',
                          ...(numeric ? numberStyle : {}),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination
            page={table.getState().pagination.pageIndex}
            pageCount={table.getPageCount()}
            rowCount={filtered}
            onChange={(next) => table.setPageIndex(next)}
          />
        </>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// CO-8 — the same contracted columns, at Snap's own lifetime granularity
// ---------------------------------------------------------------------------

/** Snap's lifetime window is its own choice and **differs per metric**, so the
 *  coverage is stated once on the card rather than implied by a date control
 *  that is not driving this table. The specific periods are unmeasured (Q-8) —
 *  this says that plainly instead of naming a number we have not pinned. */
const LIFETIME_COVERAGE =
  "Snap's Own Running Totals. The Period Each Column Covers Is Snap's Choice, " +
  'Differs from Metric to Metric, and Reaches Further Back than Our Collected History.'

/** Why a column has no total. `VIEWERS` is the load-bearing one: Snap counts
 *  each channel's viewers once *within that channel* and never across them, so
 *  adding thirteen channels counts anyone watching two shows twice. */
const NO_TOTAL: Partial<Record<DailyColumn, string>> = {
  VIEWERS: 'People, Not Views — And the Same Person Can Watch Several Channels, so These Do Not Add Up.',
  SUBSCRIBERS: 'A Total, Not a Change, so Adding the Days or the Channels Does Not Give a Bigger Total.',
}

function LifetimeTable({
  group,
  channels,
  noteFor,
}: {
  group: LanguageGroup
  channels: string[]
  noteFor: Map<string, MetricNote>
}) {
  const query = useQuery({
    queryKey: ['channel-lifetime', group, channels.join(',')],
    queryFn: () => api.channelLifetime({ group, channels }),
  })

  const data = query.data
  const rows: ChannelLifetimeRow[] = data?.rows ?? []
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const toggle = (handle: string) =>
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(handle)) next.delete(handle)
      else next.add(handle)
      return next
    })
  // **A column Snap does not answer here is not rendered.** `Subscribers` is the
  // case: it is a running total rather than a change, so there is no lifetime
  // version of it and Snap returns zero on all 13 channels. It used to show as
  // "Not Reported" across the table, which is a column's worth of space spent
  // saying nothing. The dated windows carry it on all 1,170 rows.
  const withheld = new Set(data?.withheld ?? [])
  const shown = DAILY_COLUMNS.filter((metric) => !withheld.has(metric))

  // Totals only for what the registry marks aggregable, and only ever from the
  // server's answer — the browser holds no copy of the counting-basis rules.
  const total = (metric: DailyColumn): number | null => {
    if (!data?.aggregable[metric]) return null
    const present = rows
      .map((row) => row.metrics[metric])
      .filter((value): value is number => typeof value === 'number')
    return present.length > 0 ? present.reduce((sum, value) => sum + value, 0) : null
  }

  return (
    <Card
      title={`${GROUP_TITLES[group]}: Snap Lifetime`}
      info={LIFETIME_COVERAGE}
      action={
        <span className="text-xs" style={{ color: 'var(--text-label)' }}>
          {query.isLoading ? '' : `${rows.length} channels`}
        </span>
      }
    >
      {query.isLoading ? (
        <Skeleton rows={4} />
      ) : query.error ? (
        <Note tone="error">{(query.error as Error).message}</Note>
      ) : rows.length === 0 ? (
        <Note>No {group} Channels in Snap's Latest Lifetime Snapshot.</Note>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <SelectedBar
            selected={rows.filter((row) => picked.has(row.handle))}
            onClear={() => setPicked(new Set())}
            opener={CHANNEL_LIFETIME_OPENER}
          />
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <caption className="sr-only">
              {GROUP_TITLES[group]} At Snap's Lifetime Granularity, One Row per Channel
            </caption>
            <thead>
              <tr>
                <th scope="col" style={headCell}>
                  <input
                    type="checkbox"
                    aria-label="Select Every Channel"
                    checked={rows.length > 0 && picked.size === rows.length}
                    ref={(node) => {
                      if (node) node.indeterminate = picked.size > 0 && picked.size < rows.length
                    }}
                    onChange={() =>
                      setPicked(
                        picked.size === rows.length ? new Set() : new Set(rows.map((r) => r.handle)),
                      )
                    }
                  />
                </th>
                <th scope="col" style={headCell}>
                  Channel
                </th>
                {shown.map((metric) => {
                  const note = noteFor.get(metric)
                  const caveat = NO_TOTAL[metric] ?? note?.caveat ?? null
                  return (
                    <th key={metric} scope="col" style={numericHeadCell}>
                      {DAILY_COLUMN_LABELS[metric]}
                      {caveat && <InfoDot note={caveat} />}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.profile_id}>
                  <td style={bodyCell}>
                    <RowControls
                      row={row}
                      opener={CHANNEL_LIFETIME_OPENER}
                      selected={picked.has(row.handle)}
                      onToggle={() => toggle(row.handle)}
                    />
                  </td>
                  <th
                    scope="row"
                    style={{ ...bodyCell, textAlign: 'left', fontWeight: 500 }}
                  >
                    {row.display_label}
                  </th>
                  {shown.map((metric) => {
                    const value = row.metrics[metric]
                    return (
                      <td key={metric} className="is-num" style={numericCell}>
                        {value === null || value === undefined ? (
                          <span style={{ color: 'var(--text-label)' }}>—</span>
                        ) : (
                          formatNumber(value)
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {/* The totals row exists for the columns that may be added and
                  visibly refuses for the ones that may not, rather than being
                  omitted entirely — an absent row reads as an oversight. */}
              <tr>
                <td style={{ ...bodyCell, borderTop: '2px solid var(--border)' }} />
                <th
                  scope="row"
                  style={{ ...bodyCell, textAlign: 'left', borderTop: '2px solid var(--border)' }}
                >
                  {rows.length === 1 ? 'This Channel' : `All ${rows.length} Channels`}
                </th>
                {shown.map((metric) => {
                  const sum = total(metric)
                  return (
                    <td
                      key={metric}
                      className="is-num"
                      style={{
                        ...numericCell,
                        fontWeight: 600,
                        borderTop: '2px solid var(--border)',
                      }}
                    >
                      {sum === null ? (
                        <span style={{ color: 'var(--text-label)', fontWeight: 400 }}>—</span>
                      ) : (
                        formatNumber(sum)
                      )}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
